// AnnotationCanvas — drawing overlay (PRD §9.5 + §9.7, UI brief §6.3).
//
// Two surfaces, one component:
//   - `mode: 'share'`: share-link viewer (PRD §9.5, B2e). Reads / posts
//     via `useShareAnnotations` / `usePostShareAnnotation`.
//   - `mode: 'authed'`: authed CandidateDetail drawer (PRD §9.7, B4e2).
//     Reads / posts via `useAnnotations` / `usePostAnnotation`.
//
// Surface vocabulary (unchanged across modes):
//   - SVG overlay sized to the parent container. Existing annotations
//     at the current frame render as SVG primitives; the active draft
//     renders as a translucent preview that materializes into a stroke
//     on pointer-up.
//   - Tool palette: rect, ellipse, arrow, freehand (=`stroke`), text.
//     Names match the `StrokePrimitive.type` literal union.
//   - Color swatch (default `#ff3b30`, matching the schema default).
//
// Coordinates are persisted **normalized 0..1** against the canvas's
// bounding rect, so an annotation drawn on a 720p proxy renders
// identically on a 4K source — the renderer scales back via viewBox.
// See `annotationGeometry.ts` for the math.
//
// `canDraw` is the master switch — share links share this with
// `link.can_comment`; the authed drawer hard-codes it true (project
// members can always annotate). When false, the tool palette is hidden
// and pointer events ignore inputs, but existing annotations still
// render.

import {
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { apiErrorMessage } from '../api/client';
import {
  usePostShareAnnotation,
  type UsePostShareAnnotationVars,
} from '../api/mutations/share';
import {
  usePostAnnotation,
  type UsePostAnnotationVars,
} from '../api/mutations/review';
import {
  useShareAnnotations,
  type AnnotationOut,
} from '../api/queries/share';
import { useAnnotations } from '../api/queries/review';
import {
  buildStroke,
  normalize,
  type StrokePrimitive,
  type ToolType,
} from './annotationGeometry';

interface CommonProps {
  candidateId: string;
  /** Current frame from the video player; switches the annotation set. */
  frame: number;
  /** Master switch — annotations gate. */
  canDraw: boolean;
}

interface ShareModeProps extends CommonProps {
  mode: 'share';
  token: string;
  password?: string | null;
}

interface AuthedModeProps extends CommonProps {
  mode: 'authed';
  orgSlug: string;
  projectId: string;
  /** Live review (B7): called after this client's own draw persists,
   *  so the page can notify peers over the socket. Undefined for the
   *  ordinary CandidateDetail drawer — no behavior change there. */
  onAnnotationPosted?: (annotationId: string, frame: number) => void;
}

type AnnotationCanvasProps = ShareModeProps | AuthedModeProps;

interface CanvasDataSource {
  annotations: AnnotationOut[] | undefined;
  postStroke: (stroke: StrokePrimitive, frame: number) => void;
  postPending: boolean;
  postError: unknown;
}

const TOOLS: { value: ToolType; label: string }[] = [
  { value: 'rect', label: 'Rectangle' },
  { value: 'ellipse', label: 'Ellipse' },
  { value: 'arrow', label: 'Arrow' },
  { value: 'stroke', label: 'Freehand' },
  { value: 'text', label: 'Text' },
];

const DEFAULT_COLOR = '#ff3b30';
const DEFAULT_WIDTH = 2;

export function AnnotationCanvas(
  props: AnnotationCanvasProps
): React.JSX.Element {
  if (props.mode === 'share') {
    return <ShareAnnotationCanvas {...props} />;
  }
  return <AuthedAnnotationCanvas {...props} />;
}

function ShareAnnotationCanvas(props: ShareModeProps): React.JSX.Element {
  const password = props.password ?? null;
  const list = useShareAnnotations(
    props.token,
    props.candidateId,
    props.frame,
    password
  );
  const post = usePostShareAnnotation(
    props.token,
    props.candidateId,
    password
  );
  const source: CanvasDataSource = {
    annotations: list.data,
    postStroke: (stroke, frame) => {
      const vars: UsePostShareAnnotationVars = {
        frame,
        strokes_json: [stroke],
      };
      post.mutate(vars);
    },
    postPending: post.isPending,
    postError: post.error,
  };
  return (
    <CanvasView source={source} frame={props.frame} canDraw={props.canDraw} />
  );
}

function AuthedAnnotationCanvas(
  props: AuthedModeProps
): React.JSX.Element {
  const list = useAnnotations(
    props.orgSlug,
    props.projectId,
    props.candidateId,
    props.frame
  );
  const post = usePostAnnotation(
    props.orgSlug,
    props.projectId,
    props.candidateId
  );
  const source: CanvasDataSource = {
    annotations: list.data,
    postStroke: (stroke, frame) => {
      const vars: UsePostAnnotationVars = {
        frame,
        strokes_json: [stroke],
      };
      post.mutate(vars, {
        onSuccess: (saved) => {
          props.onAnnotationPosted?.(saved.id, frame);
        },
      });
    },
    postPending: post.isPending,
    postError: post.error,
  };
  return (
    <CanvasView source={source} frame={props.frame} canDraw={props.canDraw} />
  );
}

function CanvasView({
  source,
  frame,
  canDraw,
}: {
  source: CanvasDataSource;
  frame: number;
  canDraw: boolean;
}): React.JSX.Element {
  const [tool, setTool] = useState<ToolType>('rect');
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  // Raw 0..1 points captured during the active drag. For shape tools
  // we only keep first + last (the rest are ignored); for freehand we
  // accumulate every move. null = no active draft.
  const [draft, setDraft] = useState<[number, number][] | null>(null);

  const svgRef = useRef<SVGSVGElement | null>(null);

  function pointFromEvent(
    e: ReactPointerEvent<SVGSVGElement>
  ): [number, number] | null {
    const svg = svgRef.current;
    if (svg === null) return null;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = normalize(e.clientX - rect.left, rect.width);
    const y = normalize(e.clientY - rect.top, rect.height);
    return [x, y];
  }

  function commitDraft(points: [number, number][]): void {
    if (points.length === 0) return;
    let stroke: StrokePrimitive;
    if (tool === 'text') {
      // For text we synchronously prompt — keeps the surface simple
      // until we wire a richer inline editor in a later slice.
      const text = window.prompt('Annotation text')?.trim() ?? '';
      if (text.length === 0) return;
      stroke = buildStroke('text', points, { color, width: DEFAULT_WIDTH }, text);
    } else {
      stroke = buildStroke(tool, points, { color, width: DEFAULT_WIDTH });
    }
    source.postStroke(stroke, frame);
  }

  function onPointerDown(e: ReactPointerEvent<SVGSVGElement>): void {
    if (!canDraw) return;
    const pt = pointFromEvent(e);
    if (pt === null) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    // Text tool fires on click only — no drag needed.
    if (tool === 'text') {
      commitDraft([pt]);
      return;
    }
    setDraft([pt]);
  }

  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>): void {
    if (!canDraw || draft === null) return;
    const pt = pointFromEvent(e);
    if (pt === null) return;
    if (tool === 'stroke') {
      setDraft((prev) => (prev === null ? [pt] : [...prev, pt]));
    } else {
      // rect / ellipse / arrow — keep only first + current as we go,
      // so the preview rubber-bands instead of trailing.
      setDraft((prev) => (prev === null ? [pt] : [prev[0] ?? pt, pt]));
    }
  }

  function onPointerUp(e: ReactPointerEvent<SVGSVGElement>): void {
    if (!canDraw || draft === null) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const finalDraft = draft;
    setDraft(null);
    // A click-without-drag on a shape tool produces a zero-area
    // primitive that wouldn't render usefully — skip the POST.
    if (finalDraft.length < 2 && tool !== 'stroke') return;
    commitDraft(finalDraft);
  }

  // The draft as a StrokePrimitive used for the live preview. memo so
  // re-renders during pointermove don't reallocate the same object.
  const previewStroke = useMemo<StrokePrimitive | null>(() => {
    if (draft === null || draft.length === 0) return null;
    if (tool === 'text') return null; // committed on pointerdown
    if (draft.length < 2 && tool !== 'stroke') return null;
    return buildStroke(tool, draft, { color, width: DEFAULT_WIDTH });
  }, [draft, tool, color]);

  const existingStrokes = useMemo(
    () => flattenStrokes(source.annotations),
    [source.annotations]
  );
  const errorDetail = source.postError
    ? apiErrorMessage(source.postError, 'Could not save annotation.')
    : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="bg-bg-muted/40 relative aspect-video w-full">
        <svg
          ref={svgRef}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          // `touch-none` keeps a touch-drawn annotation from scrolling
          // the page underneath the canvas. `cursor` flips so the user
          // sees what tool they're holding.
          style={{
            touchAction: 'none',
            cursor: canDraw ? 'crosshair' : 'default',
            pointerEvents: canDraw ? 'auto' : 'none',
          }}
          role="img"
          aria-label="Annotation overlay"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {existingStrokes.map((s, i) => (
            <StrokeShape key={`existing-${i.toString()}`} stroke={s} />
          ))}
          {previewStroke !== null && (
            <StrokeShape stroke={previewStroke} preview />
          )}
        </svg>
      </div>

      {canDraw && (
        <div
          className="flex flex-wrap items-center gap-2"
          aria-label="Annotation tools"
          role="toolbar"
        >
          {TOOLS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => {
                setTool(t.value);
              }}
              aria-pressed={tool === t.value}
              className={
                tool === t.value
                  ? 'bg-accent text-accent-fg border-border-strong rounded-2 border px-2 py-1 text-11'
                  : 'border-border bg-bg-elevated text-text rounded-2 hover:bg-bg-muted border px-2 py-1 text-11 transition-colors'
              }
            >
              {t.label}
            </button>
          ))}
          <label className="text-text-muted ml-2 flex items-center gap-1 text-11">
            Color
            <input
              type="color"
              value={color}
              onChange={(e) => {
                setColor(e.target.value);
              }}
              aria-label="Stroke color"
              className="h-6 w-8 rounded border-0 bg-transparent p-0"
            />
          </label>
          {source.postPending && (
            <span className="text-text-faint text-11">Saving…</span>
          )}
          {errorDetail !== null && (
            <span role="alert" className="text-danger text-11">
              {errorDetail}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Each AnnotationOut.strokes_json is a list of primitives; flatten
 * them all into one render queue so we don't have to nest two map()s
 * at the JSX layer. The order on the wire is the order on-screen.
 */
function flattenStrokes(
  annotations: AnnotationOut[] | undefined
): StrokePrimitive[] {
  if (annotations === undefined) return [];
  const out: StrokePrimitive[] = [];
  for (const ann of annotations) {
    out.push(...ann.strokes_json);
  }
  return out;
}

interface StrokeShapeProps {
  stroke: StrokePrimitive;
  preview?: boolean;
}

/**
 * Render a single StrokePrimitive as an SVG element. Coords are 0..1
 * in the wire format; the parent SVG uses viewBox="0 0 100 100", so
 * we multiply by 100 here to keep the JSX readable (no scientific
 * notation in the DOM). `vector-effect="non-scaling-stroke"` keeps the
 * stroke width in display pixels regardless of canvas size.
 */
function StrokeShape({
  stroke,
  preview = false,
}: StrokeShapeProps): React.JSX.Element | null {
  const stroke_ = stroke.color;
  const sw = stroke.width;
  const opacity = preview ? 0.6 : 1;
  switch (stroke.type) {
    case 'rect':
      if (
        stroke.x === null || stroke.x === undefined ||
        stroke.y === null || stroke.y === undefined ||
        stroke.w === null || stroke.w === undefined ||
        stroke.h === null || stroke.h === undefined
      ) {
        return null;
      }
      return (
        <rect
          x={stroke.x * 100}
          y={stroke.y * 100}
          width={stroke.w * 100}
          height={stroke.h * 100}
          fill="none"
          stroke={stroke_}
          strokeWidth={sw}
          vectorEffect="non-scaling-stroke"
          opacity={opacity}
        />
      );
    case 'ellipse':
      if (
        stroke.x === null || stroke.x === undefined ||
        stroke.y === null || stroke.y === undefined ||
        stroke.w === null || stroke.w === undefined ||
        stroke.h === null || stroke.h === undefined
      ) {
        return null;
      }
      return (
        <ellipse
          cx={(stroke.x + stroke.w / 2) * 100}
          cy={(stroke.y + stroke.h / 2) * 100}
          rx={(stroke.w / 2) * 100}
          ry={(stroke.h / 2) * 100}
          fill="none"
          stroke={stroke_}
          strokeWidth={sw}
          vectorEffect="non-scaling-stroke"
          opacity={opacity}
        />
      );
    case 'arrow': {
      const from = stroke.from;
      const to = stroke.to;
      if (
        from === null || from === undefined || from.length < 2 ||
        to === null || to === undefined || to.length < 2
      ) {
        return null;
      }
      const fx = (from[0] ?? 0) * 100;
      const fy = (from[1] ?? 0) * 100;
      const tx = (to[0] ?? 0) * 100;
      const ty = (to[1] ?? 0) * 100;
      // Simple v-arrow head — draw the shaft as one <line>, then a
      // <polyline> for the wedge. Head size scales with stroke width so
      // a thicker line gets a proportionally heavier head.
      const angle = Math.atan2(ty - fy, tx - fx);
      const headLen = Math.max(2, sw * 2);
      const h1x = tx - headLen * Math.cos(angle - Math.PI / 6);
      const h1y = ty - headLen * Math.sin(angle - Math.PI / 6);
      const h2x = tx - headLen * Math.cos(angle + Math.PI / 6);
      const h2y = ty - headLen * Math.sin(angle + Math.PI / 6);
      return (
        <g opacity={opacity}>
          <line
            x1={fx}
            y1={fy}
            x2={tx}
            y2={ty}
            stroke={stroke_}
            strokeWidth={sw}
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={`${h1x.toString()},${h1y.toString()} ${tx.toString()},${ty.toString()} ${h2x.toString()},${h2y.toString()}`}
            fill="none"
            stroke={stroke_}
            strokeWidth={sw}
            vectorEffect="non-scaling-stroke"
          />
        </g>
      );
    }
    case 'stroke': {
      const points = stroke.points;
      if (points === null || points === undefined || points.length === 0) {
        return null;
      }
      const d = points
        .map((p, i) => {
          const x = (p[0] ?? 0) * 100;
          const y = (p[1] ?? 0) * 100;
          return `${i === 0 ? 'M' : 'L'} ${x.toString()} ${y.toString()}`;
        })
        .join(' ');
      return (
        <path
          d={d}
          fill="none"
          stroke={stroke_}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity={opacity}
        />
      );
    }
    case 'text': {
      if (
        stroke.x === null || stroke.x === undefined ||
        stroke.y === null || stroke.y === undefined ||
        stroke.text === null || stroke.text === undefined ||
        stroke.text.length === 0
      ) {
        return null;
      }
      // font_size is in user-space units of the parent viewBox. Our
      // viewBox is 0..100 so a font_size of ~3 renders ~3% of canvas
      // height — readable but not overpowering. The schema lets the
      // caller pick anything 4..200; honor it here.
      const fs = stroke.font_size ?? 16;
      return (
        <text
          x={stroke.x * 100}
          y={stroke.y * 100}
          fill={stroke_}
          fontSize={fs}
          opacity={opacity}
        >
          {stroke.text}
        </text>
      );
    }
  }
}
